import { apiClient } from './client'

export interface ConsolidationEstimate {
  llmCalls: number
  tokens: number
  retrievalQueries: number
  withinCaps: boolean
  caps: { maxLlmCalls: number; maxTokens: number; maxRetrievalQueries: number }
}

export interface ConsolidationRun {
  id: number
  status:
    | 'queued'
    | 'running'
    | 'awaiting_review'
    | 'succeeded'
    | 'failed'
    | 'budget_exceeded'
    | 'cancelled'
  phase: string
  moduleProposal?: ModuleProposal | null
  counters: {
    llmCalls: number
    tokensUsed: number
    retrievalQueries: number
    evidenceChunks: number
  }
  gaps?: Array<{ displayKey: string | null; question: string }>
  errorMessage?: string | null
}

export interface ModuleCandidate {
  name: string
  aliases: string[]
  confidence: 'high' | 'medium' | 'low'
  rationale: string
}

export interface ProposedModule extends ModuleCandidate {
  tempId: string
  action: 'keep' | 'add' | 'rename'
  matchedAreaId?: number
}

export interface ModuleProposal {
  domainAreaId: number
  modules: ProposedModule[]
  summary: { existing: number; add: number; rename: number }
}

export interface ModuleReviewDecision {
  tempId: string
  decision: 'accept' | 'reject'
  edited?: { name?: string; aliases?: string[] }
}

export interface ModuleReviewBody {
  decisions: ModuleReviewDecision[]
  added?: Array<{ name: string; aliases?: string[] }>
  deleted?: number[]
}

export interface KnowledgeItem {
  id: number
  areaId: number
  kind: string
  title: string
  statement?: string
  rationale?: string
  confidence: 'high' | 'medium' | 'low'
  requirementType?: string
  status?: string
  evidenceIds: string[]
  attributes?: Record<string, unknown>
}

export interface AreaBrief {
  areaName: string
  areaType: string
  overview: string
  items: KnowledgeItem[]
}

export const knowledgeConsolidationApi = {
  estimate: (projectId: number, areaId: number) =>
    apiClient.get<ConsolidationEstimate>(
      `/projects/${projectId}/knowledge/areas/${areaId}/consolidate/estimate`
    ),
  start: (projectId: number, areaId: number, mode: 'full' | 'incremental') =>
    apiClient.post<{ runId: number }>(
      `/projects/${projectId}/knowledge/areas/${areaId}/consolidate`,
      { mode }
    ),
  getRun: (projectId: number, runId: number) =>
    apiClient.get<ConsolidationRun>(`/projects/${projectId}/knowledge/consolidation-runs/${runId}`),
  cancel: (projectId: number, runId: number) =>
    apiClient.post<{ cancelled: boolean }>(
      `/projects/${projectId}/knowledge/consolidation-runs/${runId}/cancel`,
      {}
    ),
  submitModuleReview: (projectId: number, runId: number, body: ModuleReviewBody) =>
    apiClient.post<{ runId: number; phase: string }>(
      `/projects/${projectId}/knowledge/consolidation-runs/${runId}/module-review`,
      body
    ),
  getBrief: (projectId: number, areaId: number, detail = 'standard') =>
    apiClient.get<AreaBrief>(
      `/projects/${projectId}/knowledge/areas/${areaId}/brief?detail=${detail}`
    ),
  listItems: (projectId: number, areaId: number, kind?: string) =>
    apiClient.get<KnowledgeItem[]>(
      `/projects/${projectId}/knowledge/areas/${areaId}/items${kind ? `?kind=${kind}` : ''}`
    ),
  getItem: (projectId: number, itemId: number) =>
    apiClient.get<KnowledgeItem>(`/projects/${projectId}/knowledge/items/${itemId}`),
}
